import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  VStack,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useToast,
  Spinner,
  Center,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/ApiService';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCategories: 0,
    totalContent: 0
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentContent, setRecentContent] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Fetch statistics
      const [usersRes, categoriesRes, contentRes] = await Promise.all([
        apiService({ method: 'GET', url: '/users?limit=1' }),
        apiService({ method: 'GET', url: '/categories?limit=1' }),
        apiService({ method: 'GET', url: '/content?limit=1' })
      ]);

      setStats({
        totalUsers: usersRes.data.pagination.total_items,
        totalCategories: categoriesRes.data.pagination.total_items,
        totalContent: contentRes.data.pagination.total_items
      });

      // Fetch recent users
      const recentUsersRes = await apiService({ 
        method: 'GET', 
        url: '/users?limit=5&sort=created_at:desc' 
      });
      setRecentUsers(recentUsersRes.data.users);

      // Fetch recent content
      const recentContentRes = await apiService({ 
        method: 'GET', 
        url: '/content?limit=5&sort=created_at:desc' 
      });
      setRecentContent(recentContentRes.data.content);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('ไม่สามารถโหลดข้อมูลได้');
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถโหลดข้อมูลภาพรวมได้',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'green' : 'red';
  };

  const getStatusText = (isActive) => {
    return isActive ? 'ใช้งาน' : 'ไม่ใช้งาน';
  };

  if (isLoading) {
    return (
      <Center h="400px">
        <VStack>
          <Spinner size="xl" color="blue.500" />
          <Text>กำลังโหลดข้อมูล...</Text>
        </VStack>
      </Center>
    );
  }

  if (error) {
    return (
      <Alert status="error" rounded="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Statistics Cards */}
      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={6} mb={8}>
        <GridItem>
          <Stat bg="white" p={6} rounded="lg" shadow="sm">
            <StatLabel>ผู้ใช้ทั้งหมด</StatLabel>
            <StatNumber color="blue.500">{stats.totalUsers}</StatNumber>
            <StatHelpText>
              <StatArrow type="increase" />
              ผู้ใช้ในระบบ
            </StatHelpText>
          </Stat>
        </GridItem>
        
        <GridItem>
          <Stat bg="white" p={6} rounded="lg" shadow="sm">
            <StatLabel>หมวดหมู่ทั้งหมด</StatLabel>
            <StatNumber color="green.500">{stats.totalCategories}</StatNumber>
            <StatHelpText>
              <StatArrow type="increase" />
              หมวดหมู่เนื้อหา
            </StatHelpText>
          </Stat>
        </GridItem>
        
        <GridItem>
          <Stat bg="white" p={6} rounded="lg" shadow="sm">
            <StatLabel>เนื้อหาทั้งหมด</StatLabel>
            <StatNumber color="purple.500">{stats.totalContent}</StatNumber>
            <StatHelpText>
              <StatArrow type="increase" />
              เนื้อหาที่เผยแพร่
            </StatHelpText>
          </Stat>
        </GridItem>
        
      </Grid>

      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
        {/* Recent Users */}
        <GridItem>
          <Box bg="white" p={6} rounded="lg" shadow="sm">
            <HStack justify="space-between" mb={4}>
              <Text fontSize="lg" fontWeight="bold">ผู้ใช้ล่าสุด</Text>
              <Button 
                size="sm" 
                colorScheme="blue" 
                variant="outline"
                onClick={() => navigate('/admin/users')}
              >
                ดูทั้งหมด
              </Button>
            </HStack>
            
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>ชื่อผู้ใช้</Th>
                  <Th>บทบาท</Th>
                  <Th>สถานะ</Th>
                </Tr>
              </Thead>
              <Tbody>
                {recentUsers.map((user) => (
                  <Tr key={user.id}>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">{user.full_name}</Text>
                        <Text fontSize="sm" color="gray.500">{user.username}</Text>
                      </VStack>
                    </Td>
                    <Td>
                      <Badge colorScheme={user.role === 'admin' ? 'red' : 'blue'}>
                        {user.role === 'admin' ? 'ผู้ดูแล' : 'ผู้ใช้'}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme={getStatusColor(user.is_active)}>
                        {getStatusText(user.is_active)}
                      </Badge>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </GridItem>

        {/* Recent Content */}
        <GridItem>
          <Box bg="white" p={6} rounded="lg" shadow="sm">
            <HStack justify="space-between" mb={4}>
              <Text fontSize="lg" fontWeight="bold">เนื้อหาล่าสุด</Text>
              <Button 
                size="sm" 
                colorScheme="blue" 
                variant="outline"
                onClick={() => navigate('/admin/content')}
              >
                ดูทั้งหมด
              </Button>
            </HStack>
            
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>หัวข้อ</Th>
                  <Th>หมวดหมู่</Th>
                  <Th>สถานะ</Th>
                </Tr>
              </Thead>
              <Tbody>
                {recentContent.map((content) => (
                  <Tr key={content.id}>
                    <Td>
                      <Text fontWeight="medium" noOfLines={2}>
                        {content.title}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm" color="gray.500">
                        {content.category_title || 'ไม่มีหมวดหมู่'}
                      </Text>
                    </Td>
                    <Td>
                      <Badge colorScheme={getStatusColor(content.is_active)}>
                        {getStatusText(content.is_active)}
                      </Badge>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;

